import type { Category, Transaction } from "../api/types";
import { formatDate, formatDateTime } from "../lib/money";
import { Amount, kindColor } from "./common";

/** Human-facing label for a ledger entry kind. */
const KIND_LABEL: Record<Transaction["kind"], string> = {
  deposit: "Deposit",
  withdrawal: "Spent",
  allowance: "Allowance",
  adjustment: "Adjustment",
  transfer: "Transfer",
};

export function TransactionList({
  transactions,
  categories,
  canEdit,
  onEdit,
  onDelete,
  emptyMessage,
}: {
  transactions: Transaction[];
  categories: Category[];
  canEdit: boolean;
  onEdit?: (tx: Transaction) => void;
  onDelete?: (tx: Transaction) => void;
  emptyMessage: string;
}) {
  if (transactions.length === 0) {
    return <p className="empty">{emptyMessage}</p>;
  }

  const byId = new Map(categories.map((c) => [c.id, c]));

  return (
    <ul className="tx-list">
      {transactions.map((tx) => {
        const category = tx.categoryId ? byId.get(tx.categoryId) : undefined;
        // Dated in the future: recorded, but not counted in the balance yet.
        const scheduled = new Date(tx.occurredAt).getTime() > Date.now();
        return (
          <li key={tx.id} className={`tx ${scheduled ? "tx--scheduled" : ""}`}>
            <div className="tx__main">
              <div className="tx__note">
                {tx.note || KIND_LABEL[tx.kind]}
              </div>
              <div className="tx__meta">
                <span>{scheduled ? formatDateTime(tx.occurredAt) : formatDate(tx.occurredAt)}</span>
                {scheduled ? (
                  <span className="pill pill--scheduled">Scheduled</span>
                ) : null}
                <span>·</span>
                <span>{KIND_LABEL[tx.kind]}</span>
                {category ? (
                  <span
                    className="pill"
                    style={
                      {
                        "--pill-color": kindColor(category.kind, category.color),
                      } as React.CSSProperties
                    }
                  >
                    <span className="pill__dot" />
                    {category.name}
                  </span>
                ) : null}
              </div>
            </div>

            <Amount cents={tx.amountCents} showSign />

            {canEdit ? (
              <div className="tx__actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  onClick={() => onEdit?.(tx)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn--danger btn--small"
                  onClick={() => onDelete?.(tx)}
                >
                  Delete
                </button>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
